import { takeLatest } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects'; // call, put, select, take
import createWebAPI from './WebAPI';
import createThrottler from './throttler';

const savedKeywordThrottler = createThrottler({ delay: 2000 });

const GET_MORE_SAVED = 'user-dashboard/saved/GET_MORE_SAVED';
export const getMoreSaved = (options = {}) => ({ type: GET_MORE_SAVED, options });

const CLEAR_SAVED = 'user-dashboard/saved/CLEAR_SAVED';
export const clearSaved = () => ({ type: CLEAR_SAVED });

const DELETE_SAVED = 'user-dashboard/saved/DELETE_SAVED';
export const deleteSaved = (value) => ({ type: DELETE_SAVED, value });

const SET_SAVED_LOADED = 'user-dashboard/saved/SET_SAVED_LOADED';
export const setSavedLoaded = value => ({ type: SET_SAVED_LOADED, value });

const SET_SAVED_ALL_LOADED = 'user-dashboard/saved/SET_SAVED_ALL_LOADED';
export const setSavedAllLoaded = value => ({ type: SET_SAVED_ALL_LOADED, value });

const LOAD_SAVED_PARTIAL_RESULT = 'user-dashboard/saved/LOAD_SAVED_PARTIAL_RESULT';
export const loadSavedPartialResult = items => ({ type: LOAD_SAVED_PARTIAL_RESULT, items });

const SET_SAVED_KEYWORD_FILTER = 'user-dashboard/saved/SET_SAVED_KEYWORD_FILTER';
export const setSavedKeywordFilter = value => ({ type: SET_SAVED_KEYWORD_FILTER, value });

const ADD_TO_SELECTION = 'user-dashboard/saved/ADD_TO_SELECTION';
export const addToSelection = value => ({ type: ADD_TO_SELECTION, value });

const REMOVE_FROM_SELECTION = 'user-dashboard/saved/REMOVE_FROM_SELECTION';
export const removeFromSelection = value => ({ type: REMOVE_FROM_SELECTION, value });

const initialState = {
  saved: [],
  savedPagesLoaded: 0,
  savedToShow: 10,
  savedAllLoaded: false,
  savedLoaded: false,
  savedKeywordFilter: '',
  previousSavedKeywordFilter: '',
  selection: {},
  resourceIds: [],
};

const webApi = createWebAPI();

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_SAVED_LOADED: {
      return { ...state, savedLoaded: action.value };
    }
    case SET_SAVED_ALL_LOADED: {
      return { ...state, savedAllLoaded: action.value };
    }
    case LOAD_SAVED_PARTIAL_RESULT: {
      return !!action.items && action.items.length > 0
        ? { ...state, saved: state.saved.concat(action.items) }
        : { ...state };
    }
    case CLEAR_SAVED: {
      return { ...state, saved: [], savedPagesLoaded: 0, savedAllLoaded: false };
    }
    case DELETE_SAVED: {
      return { ...state, saved: state.saved.filter(item => item.resource._id !== action.value) }; // eslint-disable-line no-underscore-dangle
    }
    case SET_SAVED_KEYWORD_FILTER: {
      return { ...state, savedKeywordFilter: action.value, previousSavedKeywordFilter: state.savedKeywordFilter };
    }
    case ADD_TO_SELECTION: {
      return { ...state, selection: { ...state.selection, [action.value]: true } };
    }
    case REMOVE_FROM_SELECTION: {
      const tempSelection = { ...state.selection };
      delete tempSelection[action.value];
      return { ...state, selection: tempSelection };
    }
    default:
      return state;
  }
}

export function sagas({ store }) {
  function* getMoreSavedSaga(action) {
    try {
      const { savedLoaded, savedAllLoaded, savedKeywordFilter } = yield select((state) => ({
        savedLoaded: state.savedModule.savedLoaded,
        savedToShow: state.savedModule.savedToShow,
        saved: state.savedModule.saved,
        savedAllLoaded: state.savedModule.savedAllLoaded,
        savedKeywordFilter: state.savedModule.savedKeywordFilter,
      }));
      if (savedLoaded && savedAllLoaded) return;
      const limit = 50;
      const params = {
        skip: 0,
        limit,
      };
      if (savedKeywordFilter && !action.options.ignoreKeyword) params.keyword = savedKeywordFilter;

      yield put(setSavedLoaded(false));

      let result = null;
      while (result === null || (result && result.length > 0)) {
        result = yield call(() => webApi.saved.getSavedResources(params));
        yield put(loadSavedPartialResult(result));
        params.skip += limit;
      }
      yield put(setSavedLoaded(true));
      yield put(setSavedAllLoaded(true));
    } catch (ex) {
      // eslint-disable-next-line no-console
      console.error('Exception in get more saved saga', ex);
    }
  }

  function* setSavedKeywordFilterSaga() {
    const { savedKeywordFilter, previousSavedKeywordFilter } = yield select((state) => ({
      savedKeywordFilter: state.savedModule.savedKeywordFilter,
      previousSavedKeywordFilter: state.savedModule.previousSavedKeywordFilter,
    }));

    const deletedThirdChar = savedKeywordFilter.length < 3 && previousSavedKeywordFilter.length >= 3;
    const shouldCallApi = savedKeywordFilter.length > 2 || deletedThirdChar;

    if (shouldCallApi) {
      savedKeywordThrottler(() => {
        store.dispatch(clearSaved());
        store.dispatch(getMoreSaved({ ignoreKeyword: savedKeywordFilter.length < 3 }));
      });
    }
  }

  function* deleteSavedSaga(action) {
    yield call(() => webApi.saved.deleteSavedResource(action.value));
  }

  return {
    * watchGetMoreSaved() { yield* takeLatest(GET_MORE_SAVED, getMoreSavedSaga); },
    * watchDeleteSaved() { yield* takeLatest(DELETE_SAVED, deleteSavedSaga); },
    * watchSetSavedKeywordFilter() { yield* takeLatest(SET_SAVED_KEYWORD_FILTER, setSavedKeywordFilterSaga); },
  };
}
